// ============================================================
// poseConstants.ts — MediaPipe Pose Landmark 索引常數
// 參考：https://ai.google.dev/edge/mediapipe/solutions/vision/pose_landmarker
// 共 33 個關鍵點，索引 0-32
// ============================================================

import {PostureGrade} from "types/posture";

/**
 * MediaPipe BlazePose 33 個關鍵點索引對應表
 * 用於在 landmarks 陣列中精確取出特定部位坐標
 */
export const POSE_LANDMARKS = {
    // ── 頭部 ───────────────────────────────────────────────
    NOSE:            0,
    LEFT_EYE_INNER:  1,
    LEFT_EYE:        2,
    LEFT_EYE_OUTER:  3,
    RIGHT_EYE_INNER: 4,
    RIGHT_EYE:       5,
    RIGHT_EYE_OUTER: 6,
    LEFT_EAR:        7,
    RIGHT_EAR:       8,
    MOUTH_LEFT:      9,
    MOUTH_RIGHT:     10,

    // ── 上身 ──────────────────────────────────────────────
    LEFT_SHOULDER:   11,
    RIGHT_SHOULDER:  12,
    LEFT_ELBOW:      13,
    RIGHT_ELBOW:     14,
    LEFT_WRIST:      15,
    RIGHT_WRIST:     16,

    // ── 手部 ──────────────────────────────────────────────
    LEFT_PINKY:      17,
    RIGHT_PINKY:     18,
    LEFT_INDEX:      19,
    RIGHT_INDEX:     20,
    LEFT_THUMB:      21,
    RIGHT_THUMB:     22,

    // ── 核心 / 髖部 ────────────────────────────────────────
    LEFT_HIP:        23,
    RIGHT_HIP:       24,

    // ── 下肢 ──────────────────────────────────────────────
    LEFT_KNEE:       25,
    RIGHT_KNEE:      26,
    LEFT_ANKLE:      27,
    RIGHT_ANKLE:     28,
    LEFT_HEEL:       29,
    RIGHT_HEEL:      30,
    LEFT_FOOT_INDEX: 31,
    RIGHT_FOOT_INDEX:32,
} as const;

// 型別：所有 Landmark 索引的聯合型別
export type LandmarkIndex = (typeof POSE_LANDMARKS)[keyof typeof POSE_LANDMARKS];

/**
 * 標準姿勢角度/偏差容差標準
 * 參考 CPEP® Certified Posture & Ergonomics Professional 評估標準
 */
export const POSTURE_STANDARDS = {
    // 頭部前傾：耳朵中心點相對肩膀中心點的 X 偏移比例（%）
    headForwardOffset: {
        excellent: 2,    // ±2% 以內：S 級
        good:      4,    // ±4% 以內：A 級
        fair:      6,    // ±6% 以內：B 級
        poor:      8,    // ±8% 以內：C 級，超過為 D
        weight:    0.25, // 佔總分比重
    },

    // 肩膀水平差：左右肩膀 Y 坐標差值的 % 比例
    shoulderLevel: {
        excellent: 2,
        good:      3,
        fair:      5,
        poor:      8,
        weight:    0.25,
    },

    // 脊椎垂直角：肩膀中心點 → 髖骨中心點的垂直偏角（度）
    spineAngle: {
        excellent: 3,    // ±3° 以內
        good:      6,
        fair:      10,
        poor:      15,
        weight:    0.35,
    },

    // 髖骨水平差：左右髖骨 Y 坐標差值的 % 比例
    hipLevel: {
        excellent: 2,
        good:      4,
        fair:      7,
        poor:      10,
        weight:    0.15,
    },
} as const;

/**
 * 各分數段對應評級閾值
 */
export const GRADE_THRESHOLDS: Record<number, PostureGrade> = {
    90: 'S',
    80: 'A',
    70: 'B',
    60: 'C',
    0:  'D',
};

/**
 * 評級對應的顯示顏色（Tailwind CSS classes）
 */
export const GRADE_COLORS: Record<string, string> = {
    S: 'text-yellow-400',   // 金色：完美
    A: 'text-green-400',    // 綠色：優秀
    B: 'text-blue-400',     // 藍色：良好
    C: 'text-orange-400',   // 橙色：待改善
    D: 'text-red-400',      // 紅色：需立即矯正
};
